import { useState } from 'react';
import { Plus, X, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Multi-day event editor: toggle, date list, and a "COPY TIERS TO ALL DATES"
 * helper that fans out the current tier list into N tiers (one per date),
 * each scoped to a single date_id.
 *
 * Reads/writes `formData.is_multi_day`, `formData.event_dates`, and
 * `formData.ticket_tiers` (adds `valid_date_ids` to each tier).
 */
export default function MultiDaySection({ formData, setFormData }) {
  const [showScope, setShowScope] = useState(false);

  const isMultiDay = !!formData.is_multi_day;
  const dates = Array.isArray(formData.event_dates) ? formData.event_dates : [];

  const genId = () => (crypto?.randomUUID?.() || `d_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const toggleMultiDay = (next) => {
    if (next) {
      // Seed with one date from the event's main date/time so the admin has
      // a starting row instead of an empty list.
      const seeded = dates.length > 0 ? dates : [{
        id: genId(),
        date: formData.date || '',
        time: formData.time || '',
        label: 'Day 1',
      }];
      setFormData({ ...formData, is_multi_day: true, event_dates: seeded });
    } else {
      // Collapsing to single-day — strip date scopes off every tier so legacy
      // check-in works + keep the dates array around in case admin re-enables
      // (don't lose data on an accidental toggle).
      const strippedTiers = (formData.ticket_tiers || []).map((t) => ({
        ...t,
        valid_date_ids: null,
      }));
      setFormData({ ...formData, is_multi_day: false, ticket_tiers: strippedTiers });
    }
  };

  const addDate = () => {
    const next = [...dates, { id: genId(), date: '', time: '', label: `Day ${dates.length + 1}` }];
    setFormData({ ...formData, event_dates: next });
  };

  const removeDate = (idx) => {
    const removed = dates[idx];
    const next = dates.filter((_, i) => i !== idx);
    // Scrub this date_id from any tier's valid_date_ids so we don't leave
    // dangling references.
    const scrubbedTiers = (formData.ticket_tiers || []).map((t) => {
      if (!Array.isArray(t.valid_date_ids)) return t;
      return { ...t, valid_date_ids: t.valid_date_ids.filter((d) => d !== removed.id) };
    });
    setFormData({ ...formData, event_dates: next, ticket_tiers: scrubbedTiers });
  };

  const updateDate = (idx, patch) => {
    const next = dates.map((d, i) => (i === idx ? { ...d, ...patch } : d));
    setFormData({ ...formData, event_dates: next });
  };

  const copyTiersToAllDates = () => {
    if (dates.length === 0) {
      toast.error('Add at least one event date first');
      return;
    }
    const baseTiers = (formData.ticket_tiers || []).filter(
      (t) => !Array.isArray(t.valid_date_ids) || t.valid_date_ids.length !== 1,
    );
    if (baseTiers.length === 0) {
      toast.error('No base tier to clone — remove per-date tiers first or add one');
      return;
    }
    // For each base tier, generate one scoped tier per date.
    const fanned = [];
    baseTiers.forEach((t) => {
      dates.forEach((d) => {
        fanned.push({
          ...t,
          id: genId(),
          name: `${t.name} · ${d.label || d.date}`,
          valid_date_ids: [d.id],
        });
      });
    });
    // Keep existing per-date tiers (len=1) + append the new fanned ones.
    const existingSingles = (formData.ticket_tiers || []).filter(
      (t) => Array.isArray(t.valid_date_ids) && t.valid_date_ids.length === 1,
    );
    setFormData({ ...formData, ticket_tiers: [...existingSingles, ...fanned] });
    toast.success(`Copied ${baseTiers.length} tier(s) across ${dates.length} date(s)`);
  };

  const toggleTierDate = (tierIdx, dateId) => {
    const tiers = [...(formData.ticket_tiers || [])];
    const t = { ...tiers[tierIdx] };
    const cur = Array.isArray(t.valid_date_ids) ? t.valid_date_ids : [];
    t.valid_date_ids = cur.includes(dateId) ? cur.filter((d) => d !== dateId) : [...cur, dateId];
    tiers[tierIdx] = t;
    setFormData({ ...formData, ticket_tiers: tiers });
  };

  return (
    <div className="border-t border-zinc-800 pt-6 space-y-4" data-testid="multi-day-section">
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={isMultiDay}
          onChange={(e) => toggleMultiDay(e.target.checked)}
          className="accent-[#B5FF00] w-5 h-5 mt-0.5"
          data-testid="multi-day-toggle"
        />
        <div>
          <div className="font-mono text-xs text-white tracking-widest">MULTI-DAY EVENT</div>
          <p className="text-zinc-500 text-xs mt-1">
            Spans multiple sessions (e.g. Fri + Sat + Sun). Buyers pick a date before choosing a ticket.
          </p>
        </div>
      </label>

      {isMultiDay && (
        <div className="pl-7 space-y-4" data-testid="multi-day-config">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono text-xs text-zinc-400 tracking-widest">EVENT DATES</label>
              <button
                type="button"
                onClick={addDate}
                className="text-[#B5FF00] font-mono text-xs flex items-center gap-1 hover:underline"
                data-testid="add-date-btn"
              >
                <Plus size={14} /> ADD DATE
              </button>
            </div>

            <div className="space-y-2">
              {dates.map((d, idx) => (
                <div
                  key={d.id}
                  className="bg-[#050505] border border-zinc-800 p-3 grid grid-cols-12 gap-2 items-end"
                  data-testid={`event-date-row-${idx}`}
                >
                  <div className="col-span-12 sm:col-span-4">
                    <label className="font-mono text-[10px] text-zinc-500 tracking-widest block mb-1">LABEL</label>
                    <input
                      type="text"
                      value={d.label || ''}
                      onChange={(e) => updateDate(idx, { label: e.target.value })}
                      placeholder={`Day ${idx + 1}`}
                      className="w-full h-9 bg-[#0A0A0A] border border-zinc-800 text-white px-2 text-sm focus:border-[#B5FF00] focus:outline-none"
                      data-testid={`event-date-label-${idx}`}
                    />
                  </div>
                  <div className="col-span-7 sm:col-span-4">
                    <label className="font-mono text-[10px] text-zinc-500 tracking-widest block mb-1">DATE</label>
                    <input
                      type="date"
                      value={d.date || ''}
                      onChange={(e) => updateDate(idx, { date: e.target.value })}
                      className="w-full h-9 bg-[#0A0A0A] border border-zinc-800 text-white px-2 text-sm focus:border-[#B5FF00] focus:outline-none"
                      data-testid={`event-date-date-${idx}`}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-3">
                    <label className="font-mono text-[10px] text-zinc-500 tracking-widest block mb-1">TIME</label>
                    <input
                      type="time"
                      value={d.time || ''}
                      onChange={(e) => updateDate(idx, { time: e.target.value })}
                      className="w-full h-9 bg-[#0A0A0A] border border-zinc-800 text-white px-2 text-sm focus:border-[#B5FF00] focus:outline-none"
                      data-testid={`event-date-time-${idx}`}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeDate(idx)}
                      className="text-zinc-500 hover:text-red-500 p-1"
                      data-testid={`remove-date-${idx}`}
                      title="Remove"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {dates.length === 0 && (
                <p className="text-zinc-500 text-xs">No dates yet. Click ADD DATE above.</p>
              )}
            </div>
          </div>

          {/* Tier → dates scope mapper */}
          <div className="bg-[#050505] border border-zinc-800 p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-xs text-white tracking-widest">TIER DATE SCOPE</div>
                <p className="text-zinc-500 text-xs mt-1">
                  By default each tier is valid on ALL dates. For single-day tickets scope a tier to one date; for passes tick multiple.
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={copyTiersToAllDates}
                  className="font-mono text-[11px] text-[#B5FF00] hover:underline flex items-center gap-1 tracking-wider"
                  data-testid="copy-tiers-btn"
                  title="Clone every current tier to one copy per date"
                >
                  <Copy size={12} /> CLONE ACROSS DATES
                </button>
                <button
                  type="button"
                  onClick={() => setShowScope(!showScope)}
                  className="font-mono text-[11px] text-zinc-400 hover:text-white tracking-wider"
                  data-testid="toggle-tier-scope-btn"
                >
                  {showScope ? 'HIDE' : 'SHOW'} SCOPE
                </button>
              </div>
            </div>

            {showScope && (
              <div className="mt-3 space-y-2" data-testid="tier-scope-editor">
                {(formData.ticket_tiers || []).length === 0 ? (
                  <p className="text-zinc-500 text-xs">Add ticket tiers above first.</p>
                ) : (
                  (formData.ticket_tiers || []).map((t, tIdx) => {
                    const scoped = Array.isArray(t.valid_date_ids) && t.valid_date_ids.length > 0;
                    const scopeType = !scoped
                      ? 'ALL DATES'
                      : t.valid_date_ids.length === 1 ? 'SINGLE DAY' : `${t.valid_date_ids.length}-DAY PASS`;
                    return (
                      <div key={t.id || tIdx} className="bg-[#0A0A0A] border border-zinc-900 p-2" data-testid={`tier-scope-row-${tIdx}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-sm font-['Josefin_Sans']">{t.name || '(unnamed tier)'}</span>
                          <span className="px-1.5 py-0.5 text-[10px] font-mono tracking-wider bg-[#B5FF00]/10 text-[#B5FF00] border border-[#B5FF00]/30">
                            {scopeType}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {dates.map((d) => {
                            const on = Array.isArray(t.valid_date_ids) && t.valid_date_ids.includes(d.id);
                            return (
                              <label
                                key={d.id}
                                className={`px-2 py-1 border cursor-pointer font-mono text-[11px] tracking-wider ${
                                  on
                                    ? 'border-[#B5FF00] bg-[#B5FF00]/10 text-[#B5FF00]'
                                    : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                                }`}
                                data-testid={`tier-scope-${tIdx}-${d.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => toggleTierDate(tIdx, d.id)}
                                  className="hidden"
                                />
                                {d.label || d.date || `Day ${dates.indexOf(d) + 1}`}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
